import { Input, Modal } from "antd";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addUser } from "../../../redux/slice/rfpModuleSlice/dashboard";
import { AppDispatch, RootState } from "redux/store";
import Button from "style-guide/component/Button";
import Typography from "style-guide/component/Typography";
import { IUserCreationValues } from "types/dashboard";
import { openNotificationWithIcon } from "utils/utils";

const INITIAL_VALUES: IUserCreationValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
};

interface IAddUserModalProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

const AddUserModal: React.FC<IAddUserModalProps> = ({ showModal, setShowModal }) => {
  const [data, setData] = useState<IUserCreationValues>(INITIAL_VALUES);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const { isLoadingModal } = useSelector((state: RootState) => state.applicationData);

  const closeModal = () => {
    setShowModal(false);
    setError("");
    setData(INITIAL_VALUES);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validate = () => {
    if (!data.firstName.trim() || !data.lastName.trim() || !data.email.trim() || !data.password.trim()) {
      setError("All fields are required.");
      return false;
    }
    if (!validateEmail(data.email.trim())) {
      setError("Please enter a valid email address.");
      return false;
    }
    if (data.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return false;
    }
    if (data.firstName.trim().length < 2) {
      setError("First name must be at least 2 characters.");
      return false;
    }
    if (data.lastName.trim().length < 2) {
      setError("Last name must be at least 2 characters.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    setError("");
    
    try {
      const status = await dispatch(addUser(data));
      if (status === 200) {
        openNotificationWithIcon("success", "User Created Successfully!");
        closeModal();
      } else {
        setError("Failed to create user. Please try again.");
      }
    } catch (err: unknown) {
      setError("An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled =
    isSubmitting ||
    isLoadingModal ||
    !data.firstName.trim() ||
    !data.lastName.trim() ||
    !data.email.trim() ||
    !data.password.trim();

  return (
    <Modal
      title="Add New User"
      open={showModal}
      onOk={handleSave}
      onCancel={closeModal}
      footer={null}
      width={"400px"}
      centered
      maskClosable={false}
      destroyOnClose
    >
      {/* Inputs */}
      <div className="flex flex-col gap-3 pb-3">
        <div className="flex flex-col">
          <Typography text="First Name" variant="Base" />
          <Input
            name="firstName"
            placeholder="Enter first name"
            onChange={handleChange}
            value={data.firstName}
            disabled={isSubmitting}
            autoComplete="given-name"
          />
        </div>
        <div className="flex flex-col">
          <Typography text="Last Name" variant="Base" />
          <Input
            name="lastName"
            placeholder="Enter last name"
            onChange={handleChange}
            value={data.lastName}
            disabled={isSubmitting}
            autoComplete="family-name"
          />
        </div>
        <div className="flex flex-col">
          <Typography text="Email" variant="Base" />
          <Input
            name="email"
            placeholder="Enter email address"
            type="email"
            onChange={handleChange}
            value={data.email}
            disabled={isSubmitting}
            autoComplete="email"
          />
        </div>
        <div className="flex flex-col">
          <Typography text="Password" variant="Base" />
          <Input.Password
            name="password"
            placeholder="Enter password"
            onChange={handleChange}
            value={data.password}
            disabled={isSubmitting}
            autoComplete="new-password"
          />
        </div>
        {error && (
          <Typography text={error} variant="Small" className="text-red-500" />
        )}
      </div>
      {/* Footer Buttons */}
      <div className="flex justify-end gap-2 pb-[20px]">
        <Button
          buttonText="Cancel"
          type={!isSubmitting ? "Default" : "Disabled"}
          onClick={closeModal}
        />
        <Button
          buttonText={isSubmitting ? "Creating..." : "Create User"}
          type={!isSaveDisabled ? "Primary" : "Disabled"}
          onClick={!isSaveDisabled ? handleSave : undefined}
        />
      </div>
    </Modal>
  );
};

export default AddUserModal;